// components/Enrollment/EnrollmentRequestsPage.js
// Admin page to view and manage student enrollment requests
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import {
  CheckCircle2, XCircle, Users, Clock, CreditCard, ShieldX,
  GraduationCap, ClipboardList, ChevronLeft, ChevronRight,
  AlertTriangle, UserCheck, CalendarDays, Info, Ban,
} from 'lucide-react';
import { AppButton, AppBadge, EmptyState, SearchInput, FilterSelect, StatsCard, PageHeader } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { getEnrollmentRequests, approveRequest, rejectRequest, getCourseBatchesForEnrollment } from '../../utils/enrollmentApi';
import './_enrollment-pages.scss';

const STATUS_CFG = {
  pending:  { color: 'amber',   label: 'Pending',  icon: Clock },
  approved: { color: 'emerald', label: 'Approved', icon: CheckCircle2 },
  paid:     { color: 'emerald', label: 'Paid',     icon: CreditCard },
  rejected: { color: 'rose',    label: 'Rejected', icon: Ban },
  expired:  { color: 'violet',  label: 'Expired',  icon: AlertTriangle },
};

const PAGE_SIZE = 15;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function EnrollmentRequestsPage() {
  /* ── state ── */
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, paid: 0, rejected: 0 });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [rejectModal, setRejectModal] = useState({ show: false, id: null, studentName: '', courseName: '', reason: '' });
  const [approveModal, setApproveModal] = useState({ show: false, id: null, studentName: '', courseName: '', courseId: null, currentBatchName: null, batchId: '' });
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  /* ── debounced search ── */
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  /* ── fetch ── */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (courseFilter) params.courseId = courseFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getEnrollmentRequests(params);
      const d = res.data || {};
      setRows(d.data || []);
      if (d.stats) setStats(d.stats);
      if (d.courses) setCourses(d.courses);
      setTotal(d.total || 0);
      setTotalPages(d.totalPages || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, courseFilter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  /* ── filter helpers ── */
  const handleStatusFilter = (v) => { setStatusFilter(v); setPage(1); };
  const handleCourseFilter = (v) => { setCourseFilter(v); setPage(1); };

  /* ── approve modal ── */
  const openApproveModal = async (r) => {
    setApproveModal({
      show: true,
      id: r._id,
      studentName: r.studentId?.name || 'Student',
      courseName: r.courseId?.name || 'Course',
      courseId: r.courseId?._id,
      currentBatchName: r.batchId?.name || null,
      batchId: r.batchId?._id || '',
    });
    if (r.courseId?._id) {
      try {
        setBatchLoading(true);
        const res = await getCourseBatchesForEnrollment(r.courseId._id);
        setBatchOptions(res.data?.data || []);
      } catch { setBatchOptions([]); }
      finally { setBatchLoading(false); }
    }
  };

  const handleApprove = async () => {
    const { id, batchId } = approveModal;
    try {
      setActionLoading(id);
      await approveRequest(id, batchId ? { batchId } : {});
      setToast({ show: true, message: 'Request approved — student enrolled successfully.', type: 'success' });
      setApproveModal(prev => ({ ...prev, show: false }));
      load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to approve.', type: 'error' });
    } finally { setActionLoading(null); }
  };

  /* ── reject modal ── */
  const openRejectModal = (r) => {
    setRejectModal({
      show: true,
      id: r._id,
      studentName: r.studentId?.name || 'Student',
      courseName: r.courseId?.name || 'Course',
      reason: '',
    });
  };

  const handleReject = async () => {
    const { id, reason } = rejectModal;
    try {
      setActionLoading(id);
      await rejectRequest(id, { rejectReason: reason });
      setToast({ show: true, message: 'Request rejected.', type: 'success' });
      setRejectModal(prev => ({ ...prev, show: false }));
      load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to reject.', type: 'error' });
    } finally { setActionLoading(null); }
  };

  /* ── pagination helpers ── */
  const getPageNumbers = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > totalPages) { end = totalPages; start = Math.max(1, end - max + 1); }
    if (start > 1) { pages.push(1); if (start > 2) pages.push('…'); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push('…'); pages.push(totalPages); }
    return pages;
  };

  /* ── showing range text ── */
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  /* ── render ── */
  return (
    <div className="enr-admin">
      <PageHeader
        icon={GraduationCap}
        title="Enrollment Requests"
        description="Track and manage all student course enrollment requests"
      />

      {/* ── Stats ── */}
      <div className="enr-admin__stats">
        <StatsCard title="Total Requests" value={stats.total} icon={ClipboardList} iconColor="blue" gradient="blue" />
        <StatsCard title="Pending" value={stats.pending} icon={Clock} iconColor="amber" gradient="amber" />
        <StatsCard title="Approved" value={stats.approved} icon={UserCheck} iconColor="emerald" gradient="emerald" />
        <StatsCard title="Paid" value={stats.paid} icon={CreditCard} iconColor="emerald" gradient="emerald" />
        <StatsCard title="Rejected" value={stats.rejected} icon={ShieldX} iconColor="rose" gradient="rose" />
      </div>

      {/* ── Filters ── */}
      <div className="enr-admin__toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search student, email or course…" />
        <div className="enr-admin__toolbar-right">
          <FilterSelect
            value={courseFilter}
            onChange={handleCourseFilter}
            options={[{ value: '', label: 'All Courses' }, ...courses.map(c => ({ value: c._id, label: c.name }))]}
          />
          <FilterSelect
            value={statusFilter}
            onChange={handleStatusFilter}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'paid', label: 'Paid' },
              { value: 'rejected', label: 'Rejected' },
            ]}
          />
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="enr-admin__loading"><Spinner animation="border" /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users size={48} />}
          title="No enrollment requests"
          description={debouncedSearch || statusFilter || courseFilter
            ? 'Try adjusting your filters or search query.'
            : 'Requests from students will appear here once they apply for courses.'}
        />
      ) : (
        <>
          <div className="enr-admin__table-wrap">
            <table className="enr-admin__table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Batch</th>
                  <th>Requested</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="enr-admin__th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const cfg = STATUS_CFG[r.status] || STATUS_CFG.pending;
                  const StatusIcon = cfg.icon;
                  const isPending = r.status === 'pending';

                  return (
                    <tr key={r._id}>
                      {/* Student */}
                      <td>
                        <div className="enr-admin__student">
                          <div className="enr-admin__avatar">{(r.studentId?.name || '?')[0].toUpperCase()}</div>
                          <div>
                            <span className="enr-admin__name">{r.studentId?.name || '—'}</span>
                            <span className="enr-admin__sub">{r.studentId?.email || ''}</span>
                            {r.studentId?.phone && <span className="enr-admin__sub">{r.studentId.phone}</span>}
                          </div>
                        </div>
                      </td>
                      {/* Course */}
                      <td>
                        <span className="enr-admin__name">{r.courseId?.name || '—'}</span>
                        {r.courseId?.shortCode && <span className="enr-admin__sub">{r.courseId.shortCode}</span>}
                      </td>
                      {/* Batch */}
                      <td>
                        {r.batchId?.name ? (
                          <>
                            <span className="enr-admin__name">{r.batchId.name}</span>
                            <span className="enr-admin__sub">{r.batchId.status}</span>
                          </>
                        ) : (
                          <span className="enr-admin__sub-muted">No batch</span>
                        )}
                      </td>
                      {/* Date */}
                      <td>
                        <span className="enr-admin__date">
                          <CalendarDays size={13} />
                          {formatDate(r.requestedAt || r.createdAt)}
                        </span>
                      </td>
                      {/* Amount */}
                      <td>{r.amountPaid ? <strong>₹{r.amountPaid.toLocaleString('en-IN')}</strong> : <span className="enr-admin__sub-muted">—</span>}</td>
                      {/* Status */}
                      <td>
                        <AppBadge color={cfg.color} size="sm">
                          <StatusIcon size={12} /> {cfg.label}
                        </AppBadge>
                      </td>
                      {/* Actions */}
                      <td>
                        {isPending ? (
                          <div className="enr-admin__actions">
                            <AppButton size="sm" variant="default" onClick={() => openApproveModal(r)} disabled={actionLoading === r._id}>
                              <CheckCircle2 size={14} /> Approve
                            </AppButton>
                            <AppButton size="sm" variant="destructive" onClick={() => openRejectModal(r)} disabled={actionLoading === r._id}>
                              <XCircle size={14} /> Reject
                            </AppButton>
                          </div>
                        ) : (
                          <span className="enr-admin__sub">{r.resolvedBy?.name ? `by ${r.resolvedBy.name}` : '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="enr-admin__pagination">
              <span className="enr-admin__page-info">
                Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
              </span>
              <div className="enr-admin__page-controls">
                <button className="enr-admin__page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((n, i) =>
                  n === '…' ? (
                    <span key={`e${i}`} className="enr-admin__page-ellipsis">…</span>
                  ) : (
                    <button key={n} className={`enr-admin__page-btn ${n === page ? 'enr-admin__page-btn--active' : ''}`} onClick={() => setPage(n)}>
                      {n}
                    </button>
                  )
                )}
                <button className="enr-admin__page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Approve Modal ═══ */}
      <Modal show={approveModal.show} onHide={() => setApproveModal(prev => ({ ...prev, show: false }))} centered dialogClassName="enr-modal">
        <div className="enr-modal__header enr-modal__header--success">
          <div className="enr-modal__header-icon"><CheckCircle2 size={22} /></div>
          <div>
            <h5 className="enr-modal__title">Approve & Assign Batch</h5>
            <p className="enr-modal__subtitle">
              Enrolling <strong>{approveModal.studentName}</strong> into <strong>{approveModal.courseName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setApproveModal(prev => ({ ...prev, show: false }))}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className="enr-modal__info-box">
            <Info size={15} />
            <span>If no batch is selected the student will be enrolled without a batch assignment. You can assign one later.</span>
          </div>
          <label className="enr-modal__label">Select Batch</label>
          {batchLoading ? (
            <div className="enr-modal__batch-loading"><Spinner size="sm" animation="border" /> Loading batches…</div>
          ) : batchOptions.length === 0 ? (
            <p className="enr-modal__empty-text">No upcoming or ongoing batches available for this course.</p>
          ) : (
            <div className="enr-modal__batch-list">
              <div
                className={`enr-modal__batch-option ${!approveModal.batchId ? 'enr-modal__batch-option--selected' : ''}`}
                onClick={() => setApproveModal(prev => ({ ...prev, batchId: '' }))}
              >
                <span className="enr-modal__batch-radio" />
                <div className="enr-modal__batch-info">
                  <span className="enr-modal__batch-name">No batch (assign later)</span>
                </div>
              </div>
              {batchOptions.map(b => {
                const seatsLeft = b.maxStudents ? b.maxStudents - (b.strength || 0) : null;
                return (
                  <div
                    key={b._id}
                    className={`enr-modal__batch-option ${approveModal.batchId === b._id ? 'enr-modal__batch-option--selected' : ''}`}
                    onClick={() => setApproveModal(prev => ({ ...prev, batchId: b._id }))}
                  >
                    <span className="enr-modal__batch-radio" />
                    <div className="enr-modal__batch-info">
                      <span className="enr-modal__batch-name">{b.name}</span>
                      <span className="enr-modal__batch-meta">
                        <AppBadge size="sm" color={b.status === 'ongoing' ? 'emerald' : 'amber'}>{b.status}</AppBadge>
                        {seatsLeft !== null && <span>{seatsLeft} seats left</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setApproveModal(prev => ({ ...prev, show: false }))}>Cancel</AppButton>
          <AppButton variant="default" onClick={handleApprove} disabled={actionLoading === approveModal.id}>
            {actionLoading === approveModal.id ? <><Spinner size="sm" animation="border" /> Approving…</> : <><CheckCircle2 size={14} /> Approve & Enroll</>}
          </AppButton>
        </div>
      </Modal>

      {/* ═══ Reject Modal ═══ */}
      <Modal show={rejectModal.show} onHide={() => setRejectModal(prev => ({ ...prev, show: false }))} centered dialogClassName="enr-modal">
        <div className="enr-modal__header enr-modal__header--danger">
          <div className="enr-modal__header-icon"><AlertTriangle size={22} /></div>
          <div>
            <h5 className="enr-modal__title">Reject Request</h5>
            <p className="enr-modal__subtitle">
              Rejecting enrollment of <strong>{rejectModal.studentName}</strong> for <strong>{rejectModal.courseName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setRejectModal(prev => ({ ...prev, show: false }))}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className="enr-modal__info-box enr-modal__info-box--warn">
            <AlertTriangle size={15} />
            <span>This action cannot be undone. The student will be notified about the rejection.</span>
          </div>
          <label className="enr-modal__label" htmlFor="reject-reason">Reason for Rejection <span className="enr-modal__optional">(optional)</span></label>
          <textarea
            id="reject-reason"
            className="enr-modal__textarea"
            rows={4}
            value={rejectModal.reason}
            onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="e.g. Prerequisites not met, batch is full, duplicate request…"
          />
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setRejectModal(prev => ({ ...prev, show: false }))}>Cancel</AppButton>
          <AppButton variant="destructive" onClick={handleReject} disabled={actionLoading === rejectModal.id}>
            {actionLoading === rejectModal.id ? <><Spinner size="sm" animation="border" /> Rejecting…</> : <><XCircle size={14} /> Reject Request</>}
          </AppButton>
        </div>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
}
